import { useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { FormattedMessage } from 'react-intl';
import { Stack } from 'components/ui';
import { TitleTypography } from 'components/styles_components/typography';
import { useSpace } from 'contexts/space';
import { useError } from 'contexts/error';
import { useInfo } from 'contexts/info';
import { DashboardLink, hardRedirect } from 'utils/link';
import InvitationsQuery from 'queries/space/invitations';
import AcceptInvitationMutation from 'mutations/space/accept_invitation';
import RejectInvitationMutation from 'mutations/space/reject_invitation';
import ListItem from 'components/space/invitations/list_item';

const InvitationsContainer = () => {
  const { selectSpace } = useSpace();
  const setError = useError();
  const setInfo = useInfo();

  const {
    data = { invitedSpaces: { spaces: [], spacesTotal: 0 } },
    loading,
    refetch,
  } = useQuery(InvitationsQuery, {
    variables: { page: 1 },
    fetchPolicy: 'network-only',
    onError: () => setError('error.space.invitations.index'),
  });

  const [acceptMutation, { loading: accepting }] = useMutation(AcceptInvitationMutation, {
    onCompleted: ({ acceptSpaceInvitation }) => {
      if (acceptSpaceInvitation.success && acceptSpaceInvitation.spaceId) {
        setInfo('info.space.invitation.accepted');
        selectSpace(acceptSpaceInvitation.spaceId);
        hardRedirect(DashboardLink());
      } else {
        setError('error.space.invitation.accept');
      }
    },
    onError: () => setError('error.space.invitation.accept'),
  });

  const [rejectMutation, { loading: rejecting }] = useMutation(RejectInvitationMutation, {
    onCompleted: ({ rejectSpaceInvitation }) => {
      if (rejectSpaceInvitation.success) {
        setInfo('info.space.invitation.rejected');
        refetch();
      } else {
        setError('error.space.invitation.reject');
      }
    },
    onError: () => setError('error.space.invitation.reject'),
  });

  const handleAccept = useCallback(
    (space) => {
      acceptMutation({ variables: { spaceId: space.id } });
    },
    [acceptMutation]
  );

  const handleReject = useCallback(
    (space) => {
      rejectMutation({ variables: { spaceId: space.id } });
    },
    [rejectMutation]
  );

  const invitations = data.invitedSpaces.spaces;
  const disabled = accepting || rejecting;

  if (loading || invitations.length === 0) {
    return null;
  }

  return (
    <Stack spacing={2} sx={{ mb: 3 }}>
      <TitleTypography variant="h1">
        <FormattedMessage id="spaces.invitations.title" />
      </TitleTypography>
      <Stack spacing={2}>
        {invitations.map((space) => (
          <ListItem
            key={space.id}
            space={space}
            onAccept={handleAccept}
            onReject={handleReject}
            disabled={disabled}
          />
        ))}
      </Stack>
    </Stack>
  );
};

export default InvitationsContainer;
